---
title: "Email enrichment for prospects"
description: "How Chosen finds a prospect's work and personal email — an async lookup you can run on demand, plus the queue-and-auto-send behavior for outreach."
category: "sourcing"
keywords:
  - "email enrichment"
  - "find candidate email"
  - "prospect email lookup"
  - "recruiting email finder"
  - "queue outreach"
schema: "HowTo"
nav:
  section: "guides"
  order: 20
  parent: "/guides/sourcing"
---

Prospects from a Mission usually arrive without a confirmed email. Enrichment is the lookup that finds one. It runs in the background and finishes in roughly 10 to 60 seconds — you don't have to wait on the screen for it. And if you draft outreach before the address resolves, Chosen queues the message and sends it the moment enrichment lands.

## What it is

When you [find a prospect](/guides/sourcing/find-prospects), Chosen shows a best-guess work email or a "no email available" flag. Enrichment turns that guess into a confirmed address. Chosen hands the prospect's name, company, and LinkedIn URL to an email-finding provider, which returns a work email, a personal email, both, or nothing.

It's a separate step because confirming an address costs an external lookup. You run it on the prospects you've actually decided to contact — not the whole list.

## Run enrichment

<Steps>
  <li>Open a prospect from the **Search** results or the **Prospects** table.</li>
  <li>In the prospect drawer, find the **Contact & outreach** section and click **Get email**.</li>
  <li>The status switches to "Finding email…". You can close the drawer — the lookup keeps running.</li>
  <li>When it finishes, the confirmed work and personal emails appear, each with a copy button.</li>
</Steps>

A prospect needs a LinkedIn URL or a company name for enrichment to have anything to work with. Without either, the **Get email** button stays disabled.

## What the result can be

Enrichment has three honest outcomes:

- **Found.** A work email, a personal email, or both. These become the recipients you can send to.
- **No match.** The provider couldn't find an address. Some people don't have a discoverable work email, and that's a real answer — not a retry-until-it-works situation.
- **Failed.** Something went wrong with the lookup itself. This one you *can* retry — the button changes to **Retry email lookup**.

Enrichment also draws on a usage allowance — each lookup costs real money to run, so lookups are rate-limited. See [usage limits](/reference/usage-limits).

## Draft now, send when the email lands

You don't have to wait for enrichment to finish before writing your message. If you compose [outreach](/guides/sourcing/outreach-drafts) to a prospect whose email isn't confirmed yet, Chosen takes a different action: instead of a **Send** button, you get **Get email & send**.

That does two things at once. It kicks off enrichment if it hasn't started, and it queues your drafted message. The moment enrichment resolves an address, the queued message sends itself — no second visit, no clicking send again.

<Callout type="info" title="Queuing only works when there's a future address to wait for">
If enrichment has already come back as "no match", there's no address coming — so Chosen won't let you queue a message that could never send. You'll see the no-match result instead. For a prospect with a LinkedIn URL or company, queuing is fine: the lookup just hasn't run yet.
</Callout>

## When it gets it wrong

Enrichment is a lookup against public and third-party data, so it's a best effort, not a guarantee. A confirmed email can still be an old one if the prospect changed jobs recently and the data hasn't caught up. A "no match" doesn't always mean no email exists — it means the provider didn't find one.

When an address bounces or a "no match" blocks you, the prospect's [LinkedIn or other source links](/guides/sourcing/find-prospects) on their record are the fallback. Once you have a usable email, move on to [drafting outreach](/guides/sourcing/outreach-drafts).
